import clsx from "clsx";

import { useUser } from "@components/user-context";
import { type GetBasicMediaPlans_me_mmm_basicMediaPlansData_plans } from "@north-beam/nb-client-side";
import { useMMMClient } from "@pages/mmm/hooks/use-mmm-client";
import { PrimaryButton } from "@shared/buttons";

import { WIZARD_STEPS_AUTOBLESSED_MODELS as WIZARD_STEPS } from "../consts";

import { ChannelBreakdownList } from "./channels-step";

interface Props {
  currentIndex: number;
  selectedMediaPlan: GetBasicMediaPlans_me_mmm_basicMediaPlansData_plans | null;
}

export const ReviewAndCreateStep = ({
  currentIndex,
  selectedMediaPlan,
}: Props) => {
  const { user } = useUser();
  const [mmmClient] = useMMMClient();

  if (!selectedMediaPlan) {
    // TODO CONSIDER SHOWING ERROR TEXT
    return null;
  }

  const isDraft = selectedMediaPlan.state === "draft";

  const spendModelDataColumns = selectedMediaPlan.spend_model_data_columns;
  const kpis = selectedMediaPlan.kpis ?? [];
  const hasNoBlessedModel = isDraft && !selectedMediaPlan.blessed_model_id;

  return (
    <div>
      <div className="mb-6">
        <div className="text-lg font-semibold">
          {WIZARD_STEPS[currentIndex].title}
        </div>
        {WIZARD_STEPS[currentIndex].description && isDraft && (
          <div className="text-sm text-interstellar-1">
            {WIZARD_STEPS[currentIndex].description}
          </div>
        )}
      </div>
      <div>
        {hasNoBlessedModel && !selectedMediaPlan.connected_model_id && (
          <p className="text-sm text-mars">
            No model is available for this media plan. Please contact Northbeam
            Support and try again later!
          </p>
        )}
      </div>
      <div className="text-xs font-bold border-b border-b-[#E5E5E5] pb-1">
        Performance Goals
      </div>
      <div className={clsx("overflow-y-auto max-h-[420px] pb-6", {})}>
        {kpis.length > 0 ? (
          kpis.map((kpi) => (
            <div
              key={kpi.id}
              className="py-2 text-xs flex items-center gap-x-1.5 border-b border-b-[#E5E5E5]"
            >
              {kpi.friendly_name}
            </div>
          ))
        ) : (
          <div className="text-xs text-interstellar-1">
            No performance goals added
          </div>
        )}
      </div>
      <div className="text-xs font-bold border-b border-b-[#E5E5E5] pb-1">
        MMM CHANNELS
      </div>
      <div className={clsx("overflow-y-auto")}>
        {spendModelDataColumns.length > 0 ? (
          <ChannelBreakdownList spendModelDataColumns={spendModelDataColumns} />
        ) : (
          <div className="text-sm text-interstellar-1 py-2">
            No channels added
          </div>
        )}
      </div>
      <div className="mb-6 mt-6">
        <div className="text-xs font-bold pb-2">
          CUSTOM SPEND CHANNELS & DATA INPUTS
        </div>
        <p className="text-mars text-xs">
          Ensure that you wait 24 hours after any change to custom spend
          channels or data inputs before creating a cycle for those changes to
          populate in your model
        </p>
        <div className="flex flex-row gap-6">
          <PrimaryButton
            onClick={() =>
              window.open(
                user.pathFromRoot("/settings/data-spreadsheets"),
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            CUSTOM SPEND CHANNELS
          </PrimaryButton>
          <PrimaryButton
            onClick={() =>
              window.open(
                mmmClient?.input_google_sheet_url,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            MMM+ DATA INPUTS
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
